import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, ChevronDown, Check, Stethoscope, Sparkles, ShieldAlert } from 'lucide-react';

const STORAGE_KEY = 'ndms-compression-level';

type LevelValue = '15-20' | '20-30' | '30-40';

interface UseCase {
  id: string;
  emoji?: string;
  label: string;
  sub: string;
  level: LevelValue;
}

// Use-case → recommended level. Mapping follows standard OTC compression guidance:
// 15-20 for prevention/relief, 20-30 for venous symptoms, 30-40 for diagnosed conditions.
const USE_CASES: UseCase[] = [
  { id: 'fatigue', emoji: '\u{1F9B5}', label: 'Tired, achy or swollen legs', sub: 'On your feet all day, end-of-day heaviness', level: '15-20' },
  { id: 'travel', emoji: '✈️', label: 'Travel & long sitting', sub: 'Long flights or drives, desk work, pregnancy', level: '15-20' },
  { id: 'veins', label: 'Varicose or spider veins', sub: 'Visible veins or moderate swelling', level: '20-30' },
  { id: 'medical', label: 'Diagnosed condition or post-op', sub: 'Lymphedema, DVT, after a vein procedure', level: '30-40' },
];

const LEVELS: LevelValue[] = ['15-20', '20-30', '30-40'];

const LEVEL_INFO: Record<LevelValue, { name: string; blurb: string }> = {
  '15-20': {
    name: 'Mild',
    blurb: 'Everyday relief and prevention — tired legs, travel, standing work, and pregnancy support. Available without a prescription.',
  },
  '20-30': {
    name: 'Moderate / Firm',
    blurb: 'The most common therapeutic level — for varicose veins, moderate swelling, and recovery after minor vein procedures.',
  },
  '30-40': {
    name: 'Firm',
    blurb: 'Strong support for diagnosed venous conditions. We recommend confirming this exact level with your doctor.',
  },
};

interface Props {
  activeLevel: string | null;
  onApplyLevel: (level: string) => void;
}

export function CompressionLevelFinder({ activeLevel, onApplyLevel }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [useCaseId, setUseCaseId] = useState<string | null>(null);
  const [level, setLevel] = useState<LevelValue | null>(null);
  const [prescribedOpen, setPrescribedOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  // Restore the shopper's previous answer so a returning visitor sees their pick.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.level && LEVELS.includes(saved.level)) setLevel(saved.level);
      if (typeof saved.useCaseId === 'string') setUseCaseId(saved.useCaseId);
    } catch {
      // ignore parse / private-mode errors
    }
  }, []);

  // Persist every change.
  useEffect(() => {
    if (!level) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ useCaseId, level }));
    } catch {
      // ignore quota / private-mode errors
    }
  }, [useCaseId, level]);

  const pickUseCase = useCallback((uc: UseCase) => {
    setUseCaseId(uc.id);
    setLevel(uc.level);
    setPrescribedOpen(false);
  }, []);

  const pickLevel = useCallback((lv: LevelValue) => {
    setLevel(lv);
    setUseCaseId(null);
  }, []);

  // Therapeutic levels (20-30 and 30-40) both carry the confirm-with-your-doctor note
  const needsDoctorNote = level === '20-30' || level === '30-40' || useCaseId === 'medical';
  const applied = level !== null && activeLevel === level;

  return (
    <div className="bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe] border border-[#7B68CC]/20 rounded-2xl overflow-hidden mb-8">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-[#7B68CC]/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#7B68CC] flex items-center justify-center flex-shrink-0">
            <Activity size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-ndms-navy leading-tight">Find My Compression Level</p>
            <p className="text-[11px] text-gray-500 leading-tight">
              Not sure how firm you need? Tell us what it's for — we'll recommend a level.
            </p>
          </div>
        </div>
        <ChevronDown size={18} className={`text-gray-500 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* initial={false}: the panel is open by default, so render it at full height on
          first paint instead of animating 0→auto (that entry animation can get stuck
          if the catalogue load re-renders the section mid-animation). Toggles still animate. */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-3 border-t border-[#7B68CC]/15 space-y-4">
              {/* Use-case picker */}
              <div>
                <p className="text-xs font-semibold text-ndms-navy mb-2">What will you mainly use them for?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {USE_CASES.map((uc) => {
                    const selected = useCaseId === uc.id;
                    return (
                      <button
                        key={uc.id}
                        onClick={() => pickUseCase(uc)}
                        className={`flex items-start gap-2.5 text-left px-3 py-2.5 rounded-xl border transition-all ${
                          selected
                            ? 'bg-white border-[#7B68CC] ring-1 ring-[#7B68CC]/30 shadow-sm'
                            : 'bg-white/60 border-gray-200 hover:border-[#7B68CC]/60 hover:bg-white'
                        }`}
                      >
                        {uc.emoji && <span className="text-lg leading-none mt-0.5">{uc.emoji}</span>}
                        <span className="min-w-0">
                          <span className="block text-xs font-bold text-ndms-navy leading-tight">{uc.label}</span>
                          <span className="block text-[10px] text-gray-500 leading-snug mt-0.5">{uc.sub}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prescribed-level shortcut */}
              <div>
                <button
                  onClick={() => setPrescribedOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-[#7B68CC] hover:underline"
                >
                  <Stethoscope size={13} />
                  My doctor prescribed a specific level
                  <ChevronDown size={13} className={`transition-transform ${prescribedOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {prescribedOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-wrap gap-2 pt-2">
                        {LEVELS.map((lv) => (
                          <button
                            key={lv}
                            onClick={() => pickLevel(lv)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                              level === lv && !useCaseId
                                ? 'bg-[#7B68CC] text-white border-[#7B68CC]'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-[#7B68CC] hover:text-[#7B68CC]'
                            }`}
                          >
                            {lv} mmHg
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Recommendation result — mounts once, then updates in place as the
                  level changes (a per-level key + mode="wait" can deadlock on re-render). */}
              <AnimatePresence>
                {level && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-xl p-3.5 border border-[#7B68CC]/30 shadow-sm space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <Sparkles size={15} className="text-[#7B68CC] fill-[#7B68CC]/20 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider leading-tight">
                            Recommended
                          </p>
                          <p className="text-base font-bold text-ndms-navy leading-tight">
                            {level} mmHg <span className="text-[#7B68CC]">· {LEVEL_INFO[level].name}</span>
                          </p>
                          <p className="text-[11px] text-gray-600 leading-snug mt-1">{LEVEL_INFO[level].blurb}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => onApplyLevel(level)}
                        disabled={applied}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex-shrink-0 ${
                          applied
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : 'bg-[#7B68CC] text-white hover:bg-[#6a55b8] shadow-sm hover:shadow'
                        }`}
                      >
                        {applied ? (
                          <span className="flex items-center gap-1"><Check size={12} /> Showing</span>
                        ) : (
                          'Show these'
                        )}
                      </button>
                    </div>

                    {needsDoctorNote && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                        <ShieldAlert size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-800 leading-snug">
                          For a diagnosed condition, please confirm the exact level with your doctor. Questions?{' '}
                          <a href="tel:+13069794115" className="underline font-semibold whitespace-nowrap">306-979-4115</a>.
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Plain-language legend for all levels */}
              <div className="border-t border-[#7B68CC]/15 pt-2">
                <button
                  onClick={() => setShowLegend((v) => !v)}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-ndms-navy transition-colors"
                >
                  What do the numbers mean?
                  <ChevronDown size={13} className={`transition-transform ${showLegend ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showLegend && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 pt-2">
                        {LEVELS.map((lv) => (
                          <div key={lv} className="flex items-start gap-2.5">
                            <span className="text-[10px] font-bold text-[#7B68CC] bg-[#7B68CC]/10 rounded-md px-1.5 py-0.5 whitespace-nowrap mt-0.5">
                              {lv}
                            </span>
                            <p className="text-[11px] text-gray-600 leading-snug">
                              <span className="font-semibold text-ndms-navy">{LEVEL_INFO[lv].name}.</span> {LEVEL_INFO[lv].blurb}
                            </p>
                          </div>
                        ))}
                        <p className="text-[10px] text-gray-400 leading-snug pt-1">
                          Higher isn't "better" — the right level depends on your need. When in doubt, ask your doctor or call us.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
